import { useEffect, useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Button } from "../../design-system/Button";
import { Badge } from "../../design-system/Badge";
import { Icon } from "../../design-system/Icon";
import { fetchServerStatus, type ServerStatus } from "../../ipc/http-transport";

/**
 * Estado del servidor (Fase M8.2). Solo en la versión web/nube: muestra de un vistazo que el servidor
 * está sano — uptime, último backup, alertas y última corrida de la Vigilancia. Sirve de "healthcheck"
 * visible: si el último backup falló o la Vigilancia no corre, se ve aquí.
 */
function ago(ms: number | null | undefined): string {
  if (!ms) return "—";
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return `hace ${s}s`;
  const m = Math.round(s / 60); if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60); if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}
function uptimeLabel(sec: number): string {
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

export function ServerStatusPanel(): JSX.Element {
  const [st, setSt] = useState<ServerStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async (): Promise<void> => { setBusy(true); setSt(await fetchServerStatus()); setBusy(false); };
  useEffect(() => { void load(); const t = setInterval(() => void load(), 60_000); return () => clearInterval(t); }, []);

  const bkOk = st?.backups.last?.ok === true;
  const bkTone = !st?.backups.last ? "neutral" : bkOk ? "success" : "danger";

  return (
    <Card style={{ marginTop: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 8 }}><Icon name="pulse" size={18} /> Estado del servidor</span>}
        subtitle="Salud de tu app en la nube: tiempo activo, copias de seguridad y Vigilancia automática."
        action={st ? <Badge tone="success" dot>En línea</Badge> : <Badge tone="neutral" dot>Sin datos</Badge>}
      />
      <CardBody>
        {!st ? (
          <div className="faint" style={{ fontSize: 13 }}>{busy ? "Consultando…" : "No se pudo leer el estado (¿sesión iniciada?)."}</div>
        ) : (
          <div style={{ display: "grid", gap: "var(--sp-3)", maxWidth: 520 }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="faint">Tiempo activo</span><b className="strong">{uptimeLabel(st.uptimeSec)}</b>
            </div>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="faint">Versión</span><span>{st.version}</span>
            </div>
            <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: 0 }} />
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="row" style={{ gap: 6 }}><Icon name="shield" size={15} /> Último backup</span>
              <span className="row" style={{ gap: 8 }}>
                <Badge tone={bkTone as "success" | "danger" | "neutral"} dot>{!st.backups.last ? "Pendiente" : bkOk ? "OK" : "Falló"}</Badge>
                <span className="faint">{ago(st.backups.last?.at)}</span>
              </span>
            </div>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="faint">Copias guardadas</span><span>{st.backups.count}</span>
            </div>
            <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: 0 }} />
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="row" style={{ gap: 6 }}><Icon name="bell" size={15} /> Alertas por correo</span>
              <Badge tone={st.alerts.enabled ? "success" : "neutral"} dot>{st.alerts.enabled ? "Activas" : "Desactivadas"}</Badge>
            </div>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="row" style={{ gap: 6 }}><Icon name="search" size={15} /> Última Vigilancia</span>
              <span className="faint">{st.watch.lastRunAt ? `${ago(st.watch.lastRunAt)} · ${st.watch.lastNewFindings ?? 0} novedad(es)` : "aún no corre"}</span>
            </div>
            <div><Button variant="ghost" icon="pulse" disabled={busy} onClick={() => void load()}>{busy ? "Actualizando…" : "Actualizar"}</Button></div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
